/**
 * useConnectorLibraryPageSize - the Connectors library's Rows Per Page,
 * remembered per user (#2161).
 *
 * Stored server-side in `users.preferences.connectorLibraryPageSize`
 * (`PATCH /settings/preferences`), so it follows the user across reloads,
 * logout/login and other browsers. A choice applies immediately; the save
 * happens in the background and a failed save only loses persistence.
 */
import { useCallback, useState } from 'react';
import {
  useUpdateUserPreferences,
  useUserSettings,
} from '@/features/settings/hooks/useSettings';

export const CONNECTOR_LIBRARY_PAGE_SIZES = [10, 20, 50, 100] as const;
export const DEFAULT_CONNECTOR_LIBRARY_PAGE_SIZE = 20;

function isValidPageSize(n: unknown): n is number {
  return (CONNECTOR_LIBRARY_PAGE_SIZES as readonly unknown[]).includes(n);
}

export function useConnectorLibraryPageSize(): [number, (size: number) => void] {
  const { data } = useUserSettings();
  const updatePreferences = useUpdateUserPreferences();
  const [chosen, setChosen] = useState<number | null>(null);

  const stored = data?.preferences?.connectorLibraryPageSize;
  const pageSize =
    chosen ?? (isValidPageSize(stored) ? stored : DEFAULT_CONNECTOR_LIBRARY_PAGE_SIZE);

  const { mutate } = updatePreferences;
  const setPageSize = useCallback(
    (size: number) => {
      if (!isValidPageSize(size)) return;
      setChosen(size);
      mutate({ preferences: { connectorLibraryPageSize: size } });
    },
    [mutate],
  );

  return [pageSize, setPageSize];
}
